import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Copy, ImagePlus, Loader2, Pencil, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, resolveImage } from "@/lib/api";

const TAGS = [
  { id: "", label: "Toutes" },
  { id: "produits", label: "Produits" },
  { id: "blog", label: "Blog" },
  { id: "categories", label: "Catégories" },
  { id: "bannieres", label: "Bannières" },
  { id: "autres", label: "Autres" },
];
const EDIT_TAGS = TAGS.filter((t) => t.id);

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
}

export default function AdminMedia() {
  const [items, setItems] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [tag, setTag] = useState("");
  const [uploadTag, setUploadTag] = useState("autres");
  const [editing, setEditing] = useState(null); // full item being edited
  const [confirmDelete, setConfirmDelete] = useState(null); // { item, usages, checking, deleting }
  const [copiedId, setCopiedId] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/media", { params: { search, tag } });
      setItems(data);
    } catch {
      toast.error("Impossible de charger la bibliothèque");
    }
  }, [search, tag]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tag", uploadTag);
      await api.post("/admin/upload", fd);
      toast.success("Image ajoutée");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const copyUrl = (m) => {
    const full = resolveImage(m.url);
    navigator.clipboard.writeText(full).then(() => {
      setCopiedId(m.id);
      setTimeout(() => setCopiedId(null), 1500);
    }).catch(() => toast.error("Copie impossible"));
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/admin/media/${editing.id}`, { caption: editing.caption, tag: editing.tag });
      toast.success("Image mise à jour");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const askDelete = async (m) => {
    setConfirmDelete({ item: m, usages: null, checking: true, deleting: false });
    try {
      const { data } = await api.get(`/admin/media/${m.id}/usage`);
      setConfirmDelete((c) => (c && c.item.id === m.id ? { ...c, usages: data.usages, checking: false } : c));
    } catch {
      setConfirmDelete((c) => (c && c.item.id === m.id ? { ...c, usages: [], checking: false } : c));
    }
  };

  const doDelete = async () => {
    const m = confirmDelete.item;
    setConfirmDelete((c) => ({ ...c, deleting: true }));
    try {
      await api.delete(`/admin/media/${m.id}`, { params: { force: true } });
      toast.success("Image supprimée");
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setConfirmDelete((c) => ({ ...c, deleting: false }));
    }
  };

  return (
    <div data-testid="admin-media" className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-bark">Bibliothèque d'images</h1>
          <p className="mt-1 text-sm text-bark-soft">Téléversez, organisez et réutilisez vos images partout dans l'administration.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="media-upload-tag"
            value={uploadTag}
            onChange={(e) => setUploadTag(e.target.value)}
            className="rounded-full border border-amber-900/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-deep"
          >
            {EDIT_TAGS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          <label
            data-testid="media-upload-label"
            className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-amber-deep px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-honey ${uploading ? "opacity-60" : ""}`}
          >
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />}
            {uploading ? "Envoi…" : "Téléverser"}
            <input ref={fileRef} data-testid="media-upload-input" type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
          </label>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-mute" />
          <input
            data-testid="media-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par nom ou légende…"
            className="w-full rounded-full border border-amber-900/20 bg-white py-2.5 pl-9 pr-4 text-sm outline-none focus:border-amber-deep"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {TAGS.map((t) => (
            <button
              key={t.id}
              data-testid={`media-filter-${t.id || "all"}`}
              onClick={() => setTag(t.id)}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${tag === t.id ? "bg-bark text-white" : "border border-amber-900/15 text-bark-soft hover:border-amber-deep"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {items === null ? (
        <div className="flex items-center justify-center gap-2 py-16 text-bark-soft">
          <Loader2 className="animate-spin text-amber-deep" size={22} /> Chargement…
        </div>
      ) : items.length === 0 ? (
        <div data-testid="media-empty" className="rounded-2xl border border-dashed border-amber-900/25 bg-white/60 py-16 text-center">
          <ImagePlus size={30} className="mx-auto mb-3 text-stone-mute" />
          <p className="text-sm text-stone-mute">Aucune image pour le moment. Téléversez votre première image.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {items.map((m) => (
            <div key={m.id} data-testid={`media-card-${m.id}`} className="overflow-hidden rounded-2xl border border-amber-900/10 bg-white shadow-sm">
              <div className="relative aspect-square bg-cream">
                <img src={resolveImage(m.url)} alt={m.caption || m.original_filename} className="h-full w-full object-cover" />
                <span className="absolute left-2 top-2 rounded-full bg-bark/70 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white">
                  {TAGS.find((t) => t.id === m.tag)?.label || "Autres"}
                </span>
              </div>
              <div className="p-2.5">
                <p className="truncate text-xs font-medium text-bark" title={m.caption || m.original_filename}>{m.caption || m.original_filename}</p>
                <p className="mt-0.5 text-[11px] text-stone-mute">{formatSize(m.size)}</p>
                <div className="mt-2 flex items-center gap-1">
                  <button data-testid={`media-copy-${m.id}`} onClick={() => copyUrl(m)} title="Copier l'URL" className="flex-1 rounded-lg border border-amber-900/15 py-1.5 text-bark-soft transition-colors hover:border-amber-deep hover:text-amber-deep">
                    {copiedId === m.id ? <Check size={14} className="mx-auto text-sage" /> : <Copy size={14} className="mx-auto" />}
                  </button>
                  <button data-testid={`media-edit-${m.id}`} onClick={() => setEditing({ ...m })} title="Modifier" className="flex-1 rounded-lg border border-amber-900/15 py-1.5 text-bark-soft transition-colors hover:border-amber-deep hover:text-amber-deep">
                    <Pencil size={14} className="mx-auto" />
                  </button>
                  <button data-testid={`media-delete-${m.id}`} onClick={() => askDelete(m)} title="Supprimer" className="flex-1 rounded-lg border border-amber-900/15 py-1.5 text-terra transition-colors hover:border-terra hover:bg-terra/10">
                    <Trash2 size={14} className="mx-auto" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-bark/50 p-4 backdrop-blur-sm" onClick={() => setEditing(null)}>
          <div data-testid="media-edit-modal" className="w-full max-w-md rounded-3xl bg-cream p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-serif text-xl font-semibold text-bark">Modifier l'image</h3>
              <button onClick={() => setEditing(null)} className="rounded-full p-2 text-bark-soft hover:bg-sand"><X size={18} /></button>
            </div>
            <img src={resolveImage(editing.url)} alt="" className="mb-4 aspect-video w-full rounded-xl border border-amber-900/10 object-cover" />
            <label className="mb-1.5 block text-sm font-medium text-bark">Légende / nom</label>
            <input
              data-testid="media-edit-caption"
              value={editing.caption}
              onChange={(e) => setEditing((v) => ({ ...v, caption: e.target.value }))}
              placeholder={editing.original_filename}
              className="mb-4 w-full rounded-xl border border-amber-900/20 bg-white px-4 py-3 text-sm outline-none focus:border-amber-deep"
            />
            <label className="mb-1.5 block text-sm font-medium text-bark">Étiquette</label>
            <select
              data-testid="media-edit-tag"
              value={editing.tag}
              onChange={(e) => setEditing((v) => ({ ...v, tag: e.target.value }))}
              className="mb-5 w-full rounded-xl border border-amber-900/20 bg-white px-4 py-3 text-sm outline-none focus:border-amber-deep"
            >
              {EDIT_TAGS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            <button data-testid="media-edit-save" onClick={saveEdit} className="w-full rounded-full bg-amber-deep py-3 font-semibold text-white transition-colors hover:bg-honey">
              Enregistrer
            </button>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-bark/50 p-4 backdrop-blur-sm" onClick={() => !confirmDelete.deleting && setConfirmDelete(null)}>
          <div data-testid="media-delete-modal" className="w-full max-w-md rounded-3xl bg-cream p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-2 font-serif text-xl font-semibold text-bark">Supprimer cette image ?</h3>
            {confirmDelete.checking ? (
              <p className="flex items-center gap-2 py-4 text-sm text-bark-soft"><Loader2 size={16} className="animate-spin text-amber-deep" /> Vérification des utilisations…</p>
            ) : confirmDelete.usages && confirmDelete.usages.length > 0 ? (
              <div data-testid="media-delete-usages" className="my-3 rounded-xl border border-terra/30 bg-terra/10 p-3 text-sm text-bark">
                <p className="mb-2 font-medium text-terra">⚠️ Cette image est actuellement utilisée :</p>
                <ul className="space-y-1">
                  {confirmDelete.usages.map((u, i) => (
                    <li key={i} className="text-xs"><span className="font-medium">{u.type} :</span> {u.name}</li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-bark-soft">La supprimer laissera ces emplacements sans image.</p>
              </div>
            ) : (
              <p className="my-3 text-sm text-bark-soft">Cette image n'est utilisée nulle part. La suppression est sans risque.</p>
            )}
            <div className="mt-4 flex gap-2">
              <button onClick={() => setConfirmDelete(null)} disabled={confirmDelete.deleting} className="flex-1 rounded-full border border-amber-900/20 py-3 text-sm font-medium text-bark-soft transition-colors hover:bg-sand disabled:opacity-50">
                Annuler
              </button>
              <button data-testid="media-delete-confirm" onClick={doDelete} disabled={confirmDelete.checking || confirmDelete.deleting} className="flex-1 rounded-full bg-terra py-3 text-sm font-semibold text-white transition-colors hover:bg-terra/90 disabled:opacity-50">
                {confirmDelete.deleting ? "Suppression…" : "Supprimer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
